import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronDown, ChevronRight, Clock, Keyboard, LogOut, Menu, Monitor, Moon, Search, Settings, Sun } from 'lucide-react';
import NotificationBell from '@/components/NotificationBell';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { RANGES, useTimeRange } from '@/context/TimeRangeContext';
import { useCommandPalette } from '@/components/palette/CommandPalette';
import { usePlatformStatus, platformState, STATE_TEXT, timeAgo } from '@/hooks/useSystemStatus';
import { prettyShortcut } from '@/hooks/useKeyboardShortcuts';
import { routeTrail } from './nav';
import { useShell } from './shellContext';

const utcTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')} UTC`;
};

/** How current the intelligence is, from the platform's own status endpoint. */
function Freshness() {
  const status = usePlatformStatus();
  const { state, stale } = platformState(status);
  const d = status.data;
  const [title, detail] = STATE_TEXT[state] || STATE_TEXT.unknown;
  const tone = state === 'operational' && !stale ? 'ok' : state === 'down' || state === 'unreachable' ? 'bad' : 'warn';
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button type="button" className="sh-ctl sh-hide-md" aria-label={`Data freshness: ${title}`}>
          <span className={`sh-dot sh-dot--${tone}`} aria-hidden="true" />
          <span className="sh-ctl-text">{d?.last_ingest_at ? `Data as of ${utcTime(d.last_ingest_at)}` : title}</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={6} className="sh-pop sh-pop--text">
          <div className="sh-pop-title"><span className={`sh-dot sh-dot--${tone}`} aria-hidden="true" />{title}{stale ? ' (stale)' : ''}</div>
          <p>{detail}</p>
          {d && (
            <dl className="sh-facts">
              <div><dt>Sources answering</dt><dd>{d.sources_up} of {d.sources_total}</dd></div>
              <div><dt>Last ingest</dt><dd>{d.last_ingest_at ? `${timeAgo(d.last_ingest_at)}, ${utcTime(d.last_ingest_at)}` : 'Not yet'}</dd></div>
              <div><dt>Stories tracked</dt><dd>{Number(d.stories || 0).toLocaleString()}</dd></div>
              <div><dt>CVEs tracked</dt><dd>{Number(d.cves_tracked || 0).toLocaleString()}</dd></div>
            </dl>
          )}
          <p className="sh-pop-note">Failed sources are retried automatically. Your briefs stay available while a source is down.</p>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function RangeMenu() {
  const { range, setDays } = useTimeRange();
  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>
        <button type="button" className="sh-ctl" aria-label={`Time range: ${range.label}`}>
          <Clock className="sh-ic" aria-hidden="true" />
          <span className="sh-ctl-text sh-hide-sm">{range.label}</span>
          <span className="sh-ctl-text sh-only-sm">{range.short}</span>
          <ChevronDown className="sh-ic sh-ic--sm" aria-hidden="true" />
        </button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content align="end" sideOffset={6} className="sh-pop">
          <Dropdown.Label className="sh-pop-label">Time range for every view</Dropdown.Label>
          <Dropdown.RadioGroup value={String(range.days)} onValueChange={(v) => setDays(Number(v))}>
            {RANGES.map((r) => (
              <Dropdown.RadioItem key={r.days} value={String(r.days)} className="sh-pop-item">
                {r.label}
                <Dropdown.ItemIndicator className="sh-pop-check"><Check className="sh-ic" aria-hidden="true" /></Dropdown.ItemIndicator>
              </Dropdown.RadioItem>
            ))}
          </Dropdown.RadioGroup>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}

function UserMenu({ isAdmin }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { preference, setTheme } = useTheme();
  const { openShortcuts } = useShell();
  const email = isAdmin ? 'Administrator' : user?.email || 'Signed in';
  const initials = isAdmin ? 'AD' : (user?.email || '?').slice(0, 2).toUpperCase();
  const signOut = () => {
    if (isAdmin) { localStorage.removeItem('admin_token'); navigate('/admin/login'); }
    else { logout(); navigate('/login'); }
  };
  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>
        <button type="button" className="sh-avatar" aria-label="Account menu" data-testid="user-menu">{initials}</button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content align="end" sideOffset={6} className="sh-pop" style={{ minWidth: 240 }}>
          <Dropdown.Label className="sh-pop-label sh-pop-label--strong">{email}</Dropdown.Label>
          <Dropdown.Label className="sh-pop-label">Theme</Dropdown.Label>
          <Dropdown.RadioGroup value={preference} onValueChange={setTheme}>
            {[['dark', 'Dark', Moon], ['light', 'Light', Sun], ['system', 'Match system', Monitor]].map(([v, l, Icon]) => (
              <Dropdown.RadioItem key={v} value={v} className="sh-pop-item">
                <Icon className="sh-ic" aria-hidden="true" />{l}
                <Dropdown.ItemIndicator className="sh-pop-check"><Check className="sh-ic" aria-hidden="true" /></Dropdown.ItemIndicator>
              </Dropdown.RadioItem>
            ))}
          </Dropdown.RadioGroup>
          <Dropdown.Separator className="sh-pop-sep" />
          {!isAdmin && <Dropdown.Item className="sh-pop-item" onSelect={() => navigate('/settings')}><Settings className="sh-ic" aria-hidden="true" />Settings</Dropdown.Item>}
          <Dropdown.Item className="sh-pop-item" onSelect={openShortcuts}><Keyboard className="sh-ic" aria-hidden="true" />Keyboard shortcuts</Dropdown.Item>
          <Dropdown.Separator className="sh-pop-sep" />
          <Dropdown.Item className="sh-pop-item sh-pop-item--danger" onSelect={signOut}><LogOut className="sh-ic" aria-hidden="true" />{isAdmin ? 'Exit admin console' : 'Sign out'}</Dropdown.Item>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}

/**
 * Context bar: where you are, which period every number covers, how fresh the
 * data is, and search. One row, 48px, the same on every page.
 */
export default function Topbar({ variant = 'customer' }) {
  const { pathname } = useLocation();
  const isAdmin = variant === 'admin';
  const { open: openPalette } = useCommandPalette();
  const { openNav, openShortcuts, navOpen } = useShell();
  const trail = routeTrail(pathname, variant);
  const canSearch = Boolean(localStorage.getItem('token'));

  return (
    <header className="sh-top" data-testid="topbar">
      <button type="button" className="sh-icon-btn sh-only-sm" onClick={openNav} aria-label="Open navigation" aria-expanded={navOpen}>
        <Menu className="sh-ic" aria-hidden="true" />
      </button>
      <nav className="sh-crumbs" aria-label="Breadcrumb">
        {trail.map((t, i) => (
          <React.Fragment key={t}>
            {i > 0 && <ChevronRight className="sh-ic sh-ic--sm" aria-hidden="true" />}
            {i === trail.length - 1 ? <span aria-current="page" className="sh-crumb-current">{t}</span> : <span>{t}</span>}
          </React.Fragment>
        ))}
      </nav>
      {!isAdmin && <RangeMenu />}
      <Freshness />
      <button
        type="button"
        className="sh-search"
        onClick={openPalette}
        aria-label={canSearch ? 'Search threats, CVEs and commands' : 'Go to or run a command'}
        aria-haspopup="dialog"
        aria-keyshortcuts="Control+K Meta+K"
        data-testid="topbar-command-launcher"
      >
        <Search className="sh-ic" aria-hidden="true" />
        <span className="sh-search-text">{canSearch ? 'Search or run a command' : 'Go to or run a command'}</span>
        <kbd aria-hidden="true">{prettyShortcut('mod+k')}</kbd>
      </button>
      <button type="button" className="sh-icon-btn sh-hide-md" onClick={openShortcuts} aria-label="Keyboard shortcuts" title="Keyboard shortcuts (?)">
        <Keyboard className="sh-ic" aria-hidden="true" />
      </button>
      {!isAdmin && <NotificationBell />}
      <UserMenu isAdmin={isAdmin} />
    </header>
  );
}
